<template>
    <ValidationProvider
        v-slot="{errors, classes, passed, failed}"
        tag="div"
        :class="['text-input', ...additionalClasses]"
        :rules="rules"
        :name="name"
        :mode="validationMode"
    >
        <slot
            name="icon"
            :state="getCurrentFieldState({passed, failed})"
        />
        <input
            :id="`${name}_input`"
            v-mask="{mask, revalidate}"
            :value="value"
            :name="name"
            :disabled="disabled"
            :inputmode="inputmode"
            type="tel"
            autocomplete="off"
            :class="[
                'field-input',
                {
                    'outlined': outlined,
                    'disabled': disabled,
                },
                ...inputClasses,
                classes
            ]"
            @focus="focusHandler"
            @blur="blurHandler"
            @change="$emit('change', $event)"
            @input="$emit('input', $event.target.value)"
        >
        <label
            :class="[
                'field-label',
                {focused: isFocused},
                ...labelClasses
            ]"
            :for="`${name}_input`"
        >
            {{ label }}
            <slot name="label" />
        </label>
        <span
            :class="[
                'field-error',
                ...errorClasses
            ]"
        >
            {{ errors[0] }}
        </span>
    </ValidationProvider>
</template>

<script>
import {defineComponent, ref, watch} from 'vue';

import mask from '@/core/directives/maskDerective';
import FORM_STATES from '@/core/constants/formStates';

import '@/assets/styles/ui-elements/textInput.scss';

export default defineComponent({
    name: 'NumberInput',
    directives: {
        mask,
    },
    props: {
        value: {
            type: [String, Number, Boolean],
            required: true,
        },
        name: {
            type: String,
            default: () => '',
        },
        label: {
            type: String,
            default: () => '',
        },
        additionalClasses: {
            type: Array,
            default: () => ([]),
        },
        inputClasses: {
            type: Array,
            default: () => ([]),
        },
        labelClasses: {
            type: Array,
            default: () => ([]),
        },
        errorClasses: {
            type: Array,
            default: () => ([]),
        },
        outlined: {
            type: Boolean,
            default: () => false,
        },
        disabled: {
            type: Boolean,
            default: () => false,
        },
        rules: {
            type: [String, Object],
            default: () => '',
        },
        autocomplete: {
            type: String,
            default: () => 'off',
        },
        validationMode: {
            type: String,
            default: () => 'eager',
        },
        inputmode: {
            type: String,
            default: () => 'text',
        },
        revalidate: {
            type: Boolean,
            default: () => false,
        },
        mask: {
            type: String,
            default: () => '9999999999[99]',
        },
        maskPlaceholder: {
            type: String,
            default: () => '',
        },
    },
    emits: ['input', 'change', 'focus', 'blur'],
    setup(props, context) {
        const isFocused = ref(false);
        const fieldState = ref(FORM_STATES.INITIAL);

        function focusHandler(event) {
            fieldState.value = FORM_STATES.FOCUS;
            isFocused.value = true;
            context.emit('focus', event);
        }

        function blurHandler(event) {
            if (fieldState.value === FORM_STATES.FOCUS) {
                fieldState.value = FORM_STATES.INITIAL;
            }

            if (!props.value) {
                isFocused.value = false;
            }

            context.emit('blur', event);
        }

        function getCurrentFieldState({passed, failed}) {
            if (passed) {
                fieldState.value = FORM_STATES.SUCCESS;
            }

            if (failed) {
                fieldState.value = FORM_STATES.ERROR;
            }

            return fieldState.value;
        }

        watch(() => props.value, (newVal) => {
            isFocused.value = !!newVal;
        }, {immediate: true});

        return {
            isFocused,
            focusHandler,
            blurHandler,
            getCurrentFieldState,
        };
    },
});
</script>
